//////////////////////////////////////////////////////////////////////////////
// IMPORTS
//////////////////////////////////////////////////////////////////////////////
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config/environment';

//////////////////////////////////////////////////////////////////////////////
// TYPE DEFINITIONS
//////////////////////////////////////////////////////////////////////////////
interface ApiError {
  message: string;
  status: number;
  errors?: string[];
}

interface ApiErrorResponse {
  message?: string;
  errors?: string[];
}

//////////////////////////////////////////////////////////////////////////////
// ERROR HANDLING
//////////////////////////////////////////////////////////////////////////////
class ApiServiceError extends Error {
  status: number;
  errors?: string[];

  constructor(message: string, status: number, errors?: string[]) {
    super(message);
    this.name = 'ApiServiceError';
    this.status = status;
    this.errors = errors;
  }
}

//////////////////////////////////////////////////////////////////////////////
// CONFIGURATION
//////////////////////////////////////////////////////////////////////////////
console.log('[API Configuration] Using API URL:', config.apiUrl);

const api: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true, // Enable sending cookies for JWT
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

//////////////////////////////////////////////////////////////////////////////
// INTERCEPTORS
//////////////////////////////////////////////////////////////////////////////
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log auth-related requests for debugging
    if (config.url?.includes('/auth/')) {
      console.log(`[API] Auth request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error.message);
    return Promise.reject(new ApiServiceError(
      'Failed to send request',
      0,
      [error.message]
    ));
  }
);

// Configure response interceptor
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle network errors (like CORS or connection issues)
    if (!error.response) {
      console.error('[API] Network error:', error.message);
      return Promise.reject(new ApiServiceError(
        'Network error: Unable to connect to the server',
        0,
        [error.message]
      ));
    }

    // Handle API errors
    const errorResponse: ApiError = {
      message: error.response.data?.message || error.message || 'An unexpected error occurred',
      status: error.response.status,
      errors: error.response.data?.errors || []
    };

    console.error('[API] Response error:', errorResponse);
    return Promise.reject(new ApiServiceError(
      errorResponse.message,
      errorResponse.status,
      errorResponse.errors
    ));
  }
);

//////////////////////////////////////////////////////////////////////////////
// EXPORTS
//////////////////////////////////////////////////////////////////////////////
export default api;
